import { Draggable, Droppable } from "react-beautiful-dnd";
/** Helpers */
import clsx from "clsx";
import { useTranslation } from "react-i18next";

/** MUI */
import { Box, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
/** - icons */
import { DragIndicator } from "@mui/icons-material";

const useStyles = makeStyles((theme) => ({
    Paper: {
        ...theme.shape.boxedWrapper,
        width: "100%",
        height: "100%",
        overflow: "auto",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-start",
        flexDirection: "column",
        padding: theme.spacing(4, 2, 2.5, 2),
        // background:
        //     "linear-gradient(180deg, rgba(255,255,255,1) 10%, rgba(0,0,0,0.02) 30%, rgba(0,0,0,0.02) 100%, rgba(255,255,255,1) 100%)",
        [theme.breakpoints.down("md")]: {
            alignItems: "center",
            borderRadius: 24,
            // padding: theme.spacing(0, 1),
            padding: theme.spacing(1.5, 1, 1, 1),
            minHeight: 56,
        },
    },
    PaperDisabled: {
        borderColor: "#D62A1D",
        background:
            "linear-gradient(180deg, rgba(255,255,255,1) 10%, rgba(255,0,0,0.025) 30%, rgba(255,0,0,0.025) 100%, rgba(255,255,255,1) 100%)",
    },
    PaperAlowed: {
        borderColor: "#287E3F",
        background:
            "linear-gradient(180deg, rgba(255,255,255,1) 10%, rgba(0,255,0,0.025) 30%, rgba(0,255,0,0.025) 100%, rgba(255,255,255,1) 100%)",
    },
    name: {
        fontWeight: 600,
        textAlign: "center",
        // fontSize: "0.88888889rem",
        fontSize: "clamp(13px, 1vw, 0.88888889rem)",
        color: "rgba(0,0,0,0.87)",
        position: "absolute",
        left: "50%",
        transform: "translateX(-50%)",
        top: 5,
        padding: theme.spacing(0, 3),
        background:
            "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 20%, rgba(255,255,255,1) 80%, rgba(255,255,255,0) 100%)",
        [theme.breakpoints.down("md")]: {
            // fontSize: "0.88888889rem",
            top: 7,
        },
    },
    nameDisabled: {
        color: "#D62A1D",
    },
    nameAllowed: {
        color: "#287E3F",
    },
    item: {
        ...theme.shape.boxedWrapper,
        boxShadow: "none",
        backgroundColor: "#FFFFFF",
        minHeight: 48,
        borderRadius: 32,
        width: "100%",
        padding: theme.spacing(1.5, 3, 1.5, 2),
        display: "flex",
        alignItems: "center",
        fontSize: "clamp(14px, 1vw, 1rem)",
        "&:focus-visible": {
            outlineOffset: 0,
            outlineStyle: "solid",
            outlineWidth: 2,
            outlineColor: "#101010",
        },
        "& + &": {
            marginTop: theme.spacing(2),
        },
    },
    selected: {
        backgroundColor: "#f1edf7",
        boxShadow: "3px 12px 16px #00000066",
        "&:focus-visible": {
            ...theme.outliness,
        },
    },
}));

const Column = ({ itemsOrder, id, title, ITEMS, columnsDisabled, dragStarted, onSelect, onFocused }) => {
    const classes = useStyles();

    const { t } = useTranslation();
    const _isDropDisabled = dragStarted && columnsDisabled?.includes(id);

    return (
        <>
            <Typography
                className={clsx(
                    classes.name,
                    dragStarted && !_isDropDisabled && classes.nameAllowed,
                    _isDropDisabled && classes.nameDisabled
                )}
                aria-description={t("Sekcja")}
            >
                {title}
            </Typography>

            <Droppable droppableId={id} isDropDisabled={_isDropDisabled}>
                {(provided) => {
                    return (
                        <div
                            {...provided.draggableProps}
                            ref={provided.innerRef}
                            className={clsx(
                                classes.Paper,
                                dragStarted && !_isDropDisabled && classes.PaperAlowed,
                                _isDropDisabled && classes.PaperDisabled
                            )}
                        >
                            {itemsOrder.map((item_id, index) => {
                                const item = ITEMS[item_id];

                                return (
                                    <Draggable draggableId={item?.id} index={index} key={item?.id}>
                                        {(provided, snapshot) => {
                                            const _isSelected = snapshot.isDragging;

                                            onSelect && onSelect({ [item?.id]: _isSelected });

                                            return (
                                                <div
                                                    className={clsx(classes.item, _isSelected && classes.selected)}
                                                    {...provided.dragHandleProps}
                                                    {...provided.draggableProps}
                                                    ref={provided.innerRef}
                                                    role={"option"}
                                                    // aria-roledescription={t('Naciśnij spację, aby wejść w tryb zmiany układu tablicy')}
                                                    // aria-describedby={"rdnd-note"}
                                                    aria-describedby={"rdnd-item-note"}
                                                    onFocus={(event) => {
                                                        const _this = event?.target;
                                                        if (_this) {
                                                            _this?.setAttribute("aria-describedby", "rdnd-note");
                                                            const _thisSpan = _this?.querySelector(
                                                                `[data-item-id="${item.id}"]`
                                                            );
                                                            _thisSpan.innerHTML = "";
                                                        }

                                                        onFocused && onFocused(true);
                                                    }}
                                                    onBlur={(event) => {
                                                        const _this = event?.target;
                                                        if (_this) {
                                                            _this?.setAttribute("aria-describedby", "");
                                                            const _thisSpan = _this?.querySelector(
                                                                `[data-item-id="${item.id}"]`
                                                            );
                                                            _thisSpan.innerHTML = t(
                                                                ` - Naciśnij spację, aby wejść w tryb zmiany układu tablicy`
                                                            );
                                                        }
                                                        onFocused && onFocused(false);
                                                    }}
                                                >
                                                    <DragIndicator
                                                        color={"primary"}
                                                        sx={{ mr: 0.5, fontSize: "1.25rem" }}
                                                    />

                                                    <span>{t(item?.title)}</span>
                                                    <Box
                                                        data-item-id={item.id}
                                                        sx={{
                                                            width: "1px",
                                                            height: "1px",
                                                            overflow: "hidden",
                                                            fontSize: 8,
                                                        }}
                                                    >
                                                        {t(
                                                            ` - Naciśnij spację, aby wejść w tryb zmiany układu tablicy`
                                                        )}
                                                    </Box>
                                                </div>
                                            );
                                        }}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </div>
                    );
                }}
            </Droppable>
        </>
    );
};

export default Column;
